import React from 'react';

import { Button, Flex, Modal, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { TelephonyService } from 'services/telephony-service';
import { PhoneNumberProvider } from 'shared/interfaces/Telephony';
import { requiredInputValidator, validateIsPhoneNumber } from 'shared/utils/form-validators';

interface ImportPhoneNumberModalProps {
  opened: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

export function ImportPhoneNumberModal(importPhoneNumberModalProps: ImportPhoneNumberModalProps) {
  const { opened, onClose, onImportSuccess } = importPhoneNumberModalProps;
  const { t } = useTranslation();

  const importPhoneNumberMutation = useMutation({
    mutationFn: TelephonyService.importExternalPhoneNumber,
    onSuccess: () => {
      importExternalPhoneNumberForm.reset();
      onClose();
      onImportSuccess();
      NotificationService.showResult(true, 'FEED_BACK.TELEPHONY.IMPORT_MODAL.IMPORT_SUCCESS_MESSAGE');
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.Conflict) {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.IMPORT_MODAL.CONFLICT_ERROR_MESSAGE');
      } else if (error.response?.status === HttpStatusCode.PaymentRequired) {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.IMPORT_MODAL.USAGE_LIMIT_ERROR_MESSAGE', error);
      } else {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.IMPORT_MODAL.IMPORT_ERROR_MESSAGE');
      }
    }
  });

  const importExternalPhoneNumberForm = useForm({
    initialValues: {
      phoneNumber: '',
      accountSid: '',
      authToken: ''
    },
    validate: {
      phoneNumber: (value) => requiredInputValidator(value) || validateIsPhoneNumber(value),
      accountSid: (value) => requiredInputValidator(value),
      authToken: (value) => requiredInputValidator(value)
    }
  });

  const onImportButtonClick = () => {
    const validationErrors = importExternalPhoneNumberForm.validate();
    if (validationErrors.hasErrors) return;

    importPhoneNumberMutation.mutate({
      phoneNumberConfiguration: {
        provider: PhoneNumberProvider.TWILIO,
        accountSID: importExternalPhoneNumberForm.values.accountSid,
        authToken: importExternalPhoneNumberForm.values.authToken,
        phoneNumber: importExternalPhoneNumberForm.values.phoneNumber
      }
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.TITLE')}
      size="sm">
      <Text size="sm" mb="md">
        {t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.DESCRIPTION')}
      </Text>
      <TextInput
        label={
          <InputLabelWithInfo
            infoPosition="bottom"
            label={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.PHONE_NUMBER_LABEL')}
            infoContentTranslationKey="SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.PHONE_NUMBER_TOOLTIP"
          />
        }
        placeholder={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.PHONE_NUMBER_PLACEHOLDER')}
        mb="md"
        {...importExternalPhoneNumberForm.getInputProps('phoneNumber')}
      />
      <TextInput
        label={
          <InputLabelWithInfo
            infoPosition="bottom"
            label={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.ACCOUNT_SID_LABEL')}
            infoContentTranslationKey="SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.ACCOUNT_SID_TOOLTIP"
          />
        }
        placeholder={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.ACCOUNT_SID_PLACEHOLDER')}
        mb="md"
        {...importExternalPhoneNumberForm.getInputProps('accountSid')}
      />
      <TextInput
        label={
          <InputLabelWithInfo
            infoPosition="bottom"
            label={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.AUTH_TOKEN_LABEL')}
            infoContentTranslationKey="SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.AUTH_TOKEN_TOOLTIP"
          />
        }
        placeholder={t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.AUTH_TOKEN_PLACEHOLDER')}
        mb="xl"
        {...importExternalPhoneNumberForm.getInputProps('authToken')}
      />
      <Flex justify="flex-end" gap="sm">
        <Button variant="subtle" onClick={onClose}>
          {t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.CANCEL')}
        </Button>
        <Button onClick={onImportButtonClick} loading={importPhoneNumberMutation.isLoading}>
          {t('SMART_ASSISTANT.TELEPHONY_SECTION.IMPORT_MODAL.IMPORT')}
        </Button>
      </Flex>
    </Modal>
  );
}
